import { IQuestionary } from './intefraces';

export type KeyCode =
  | 'KeyW'
  | 'KeyH'
  | 'ArrowUp'
  | 'ArrowDown'
  | 'ArrowLeft'
  | 'ArrowRight';

export interface ScriptNode {
  id: string;
  play: () => Promise<void>;
  allowedKeys?: KeyCode[] | 'NONE' | 'ANY';
  delayBeforeAdvancingMs?: number;
  advanceOnAudioEnd?: boolean;
  awaitButtonNext?: boolean;
  /** If true, keyboard/click input is ignored until notifyAudioEnded() is called */
  awaitAudioEndBeforeInput?: boolean;
  completeOnAdvance?: boolean;

  /** If true, any primary pointerdown (left mouse / tap) will advance */
  enableClickAnywhere?: boolean;

  /** If provided, only pointerdown on elements matching these selectors will advance */
  clickSelectors?: string[];
  cbOnCompletedStep?: () => void;
  questionary?: IQuestionary;
  text?: string;
}

export interface ScriptOptions {
  onNodeStart?: (nodeId: string, index: number) => void;
  onWaitingForInput?: (nodeId: string, index: number) => void;
  onKeyCaptured?: (
    ev: KeyboardEvent | PointerEvent | { type: 'click' },
    nodeId: string,
    index: number,
  ) => void;
  onComplete?: (lastNodeId: string) => void;
  onError?: (err: unknown, nodeId?: string) => void;
  ignoreRepeat?: boolean;
  name?: string;
}

export class LinearScriptPlayer {
  private nodes: ScriptNode[];
  private idToIndex: Map<string, number>;
  private opts: Required<Pick<ScriptOptions, 'ignoreRepeat'>> & ScriptOptions;
  private currentIndex = -1;
  private running = false;
  private waiting = false;
  private completed = false;
  private keyListener?: (ev: KeyboardEvent) => void;
  private pointerAnywhereListener?: (ev: PointerEvent) => void;
  private pointerElementListeners: Array<{
    el: Element;
    fn: (ev: PointerEvent) => void;
  }> = [];
  private triggerResolver?: (res: {
    kind: 'key' | 'click';
    event: any;
  }) => void;
  private disposed = false;
  private name = '';
  private audioReady = true;

  constructor(nodes: ScriptNode[], opts: ScriptOptions = {}) {
    if (!nodes.length)
      throw new Error('LinearScriptPlayer requires at least one node.');
    const ids = new Set(nodes.map((n) => n.id));
    if (ids.size !== nodes.length)
      throw new Error('Decision nodes must have unique ids.');
    this.nodes = nodes.slice();
    this.idToIndex = new Map(this.nodes.map((n, i) => [n.id, i]));
    this.opts = { ignoreRepeat: true, ...opts };
    this.name = opts.name || '';
  }

  /** Called by the audio layer when playback ends naturally */
  notifyAudioEnded(): void {
    this.audioReady = true;
  }

  /** Public method to simulate a "Next" button press */
  triggerNext(): void {
    const currentNode = this.nodes[this.currentIndex];
    const delayMs = currentNode.delayBeforeAdvancingMs ?? 0;

    const continueToNextNode = () => {
      if (this.currentIndex === this.nodes.length - 1) {
        this.running = false;
      }
      if (!this.waiting) return;
      this.waiting = false;

      // Save resolver before listeners are torn down (teardown clears triggerResolver)
      const resolver = this.triggerResolver;
      this.triggerResolver = undefined;

      this.teardownListeners();

      if (resolver) {
        resolver({ kind: 'click', event: { type: 'click' } });
      }
    };

    if (delayMs > 0) {
      this.sleep(delayMs).then(() => {
        continueToNextNode();
      });
    } else {
      continueToNextNode();
    }
  }

  async start(startNodeId?: string): Promise<void> {
    this.ensureNotDisposed();
    if (this.running) throw new Error('LinearScriptPlayer is already running.');
    this.running = true;

    const startIndex = startNodeId != null ? this.indexForId(startNodeId) : 0;
    if (startIndex < 0)
      throw new Error(`Unknown start node id: ${startNodeId}`);

    try {
      await this.runFromIndex(startIndex);
    } catch (err) {
      this.opts.onError?.(err, this.nodes[this.currentIndex]?.id);
      throw err;
    } finally {
      this.running = false;
    }
  }

  stop(): void {
    this.ensureNotDisposed();
    this.teardownListeners();
    this.running = false;
    this.waiting = false;
  }

  complete(): void {
    this.completed = true;
    this.stop();
  }

  dispose(): void {
    if (this.disposed) return;
    this.stop();
    this.disposed = true;
  }

  getCurrentNodeId(): string | undefined {
    return this.nodes[this.currentIndex]?.id;
  }

  getMaxOptionsCount(): number {
    return this.nodes.reduce((max, n) => {
      const count = n.questionary?.options?.length ?? 0;
      return count > max ? count : max;
    }, 0);
  }

  getQuestionaryInfo(): { current: number; total: number } | null {
    const questionaryNodes = this.nodes.filter((n) => n.questionary);
    if (questionaryNodes.length === 0) return null;

    const currentNode = this.nodes[this.currentIndex];
    if (!currentNode?.questionary) return null;

    const currentQuestionNumber =
      questionaryNodes.findIndex((n) => n.id === currentNode.id) + 1;

    return { current: currentQuestionNumber, total: questionaryNodes.length };
  }

  getProgressInfo(): {
    currentStep: number;
    totalSteps: number;
    percentage: number;
  } | null {
    if (this.currentIndex < 0) return null;
    const totalSteps = this.nodes.length - 1; // last node is the completion sentinel
    if (totalSteps <= 0) return null;
    const currentStep = Math.min(this.currentIndex + 1, totalSteps);
    const percentage = Math.round((currentStep / totalSteps) * 100);
    return { currentStep, totalSteps, percentage };
  }

  getCurrentState(): {
    isCompleted: boolean;
    isRunning: boolean;
    isWaiting: boolean;
    currentNode: ScriptNode;
    name: string;
  } {
    return {
      isCompleted: this.completed,
      isRunning: this.running,
      isWaiting: this.waiting,
      currentNode: this.nodes[this.currentIndex],
      name: this.name,
    };
  }

  // ---- Internal orchestration ----

  private async runFromIndex(index: number): Promise<void> {
    while (this.running && index >= 0 && index < this.nodes.length) {
      this.currentIndex = index;
      const node = this.nodes[index];

      this.opts.onNodeStart?.(node.id, index);

      // 1) Play the node's audio
      await node.play();
      if (!this.running) break;

      // 2) Wait for keyboard OR external triggerNext()
      this.audioReady =
        !node.awaitAudioEndBeforeInput && !node.enableClickAnywhere;
      this.opts.onWaitingForInput?.(node.id, index);
      const result = await this.waitForAdvance(node);
      if (!this.running) break;

      this.opts.onKeyCaptured?.(
        result.kind === 'key' ? result.event : { type: 'click' },
        node.id,
        index,
      );

      // 3) Advance to next node
      const nextIndex = index + 1 < this.nodes.length ? index + 1 : -1;
      if (nextIndex === -1) {
        this.opts.onComplete?.(node.id);
        break;
      }

      // 4) Optional delay before next node
      const delayMs = node.delayBeforeAdvancingMs ?? 0;
      if (delayMs > 0) {
        await this.sleep(delayMs);
        if (!this.running) break;
      }

      if (node.cbOnCompletedStep) {
        node.cbOnCompletedStep();
      }
      index = nextIndex;
    }

    if (this.running) {
      this.opts.onComplete?.(
        this.nodes[this.currentIndex]?.id ?? this.nodes.at(-1)!.id,
      );
    }
  }

  private waitForAdvance(
    node: ScriptNode,
  ): Promise<{ kind: 'key' | 'click'; event: any }> {
    const allowed = node.allowedKeys === 'ANY' ? 'ANY' : node.allowedKeys; // ?? ['KeyW', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
    const ignoreRepeat = this.opts.ignoreRepeat;

    this.waiting = true;

    return new Promise((resolve) => {
      // allow triggerNext() to resolve this
      this.triggerResolver = resolve;

      // Keyboard handler
      const keyHandler = (ev: KeyboardEvent) => {
        if (!this.waiting) return;
        if (!this.audioReady) return;
        if (ignoreRepeat && ev.repeat) return;

        const code = ev.code as KeyCode;
        let isAllowed =
          !node.allowedKeys || node.allowedKeys === 'NONE'
            ? false
            : (allowed as KeyCode[]).includes(code);

        if (node.allowedKeys === 'ANY') {
          isAllowed = true;
        }
        if (!isAllowed) return;

        ev.preventDefault?.();
        this.waiting = false;
        this.teardownListeners();
        resolve({ kind: 'key', event: ev });
      };
      this.keyListener = keyHandler;
      window.addEventListener('keydown', keyHandler, { passive: false });

      // Pointer handler (mouse/touch/pen)
      const pointerHandler = (ev: PointerEvent) => {
        if (!this.waiting) return;
        if (!this.audioReady) return;
        // primary button / tap only
        if (ev.button !== 0) return;

        // Ignore clicks on interactive UI controls (e.g. camera-sharing button,
        // nav buttons, drawers) so they don't accidentally advance the script.
        const target = ev.target as Element | null;
        if (
          target?.closest?.(
            'button, a, input, select, textarea, [role="button"], [role="menuitem"], [role="link"]',
          )
        ) {
          return;
        }

        ev.preventDefault?.();
        this.waiting = false;
        this.teardownListeners();
        resolve({ kind: 'click', event: ev });
      };

      // Global click-anywhere
      if (node.enableClickAnywhere) {
        this.pointerAnywhereListener = pointerHandler;
        document.addEventListener('pointerdown', pointerHandler, {
          capture: true,
          passive: false,
        });
      }

      // Click on specific selectors only
      if (node.clickSelectors?.length) {
        for (const sel of node.clickSelectors) {
          document.querySelectorAll(sel).forEach((el) => {
            const fn = (ev: PointerEvent) => {
              if (!this.waiting) return;
              if (!this.audioReady) return;
              if (!(ev.target instanceof Node) || !el.contains(ev.target)) {
                return;
              }
              if (ev.button !== 0) return;

              ev.preventDefault?.();
              this.waiting = false;
              this.teardownListeners();
              resolve({ kind: 'click', event: ev });
            };

            el.addEventListener('pointerdown', fn as EventListener, {
              passive: false,
            });
            this.pointerElementListeners.push({ el, fn });
          });
        }
      }
    });
  }

  private teardownListeners(): void {
    if (this.keyListener) {
      window.removeEventListener('keydown', this.keyListener as EventListener);
      this.keyListener = undefined;
    }

    if (this.pointerAnywhereListener) {
      document.removeEventListener(
        'pointerdown',
        this.pointerAnywhereListener as EventListener,
        true,
      );
      this.pointerAnywhereListener = undefined;
    }

    if (this.pointerElementListeners.length) {
      for (const { el, fn } of this.pointerElementListeners) {
        el.removeEventListener('pointerdown', fn as EventListener);
      }
      this.pointerElementListeners = [];
    }

    this.triggerResolver = undefined;
  }

  private indexForId(id: string): number {
    return this.idToIndex.get(id) ?? -1;
  }

  private ensureNotDisposed() {
    if (this.disposed)
      throw new Error('LinearScriptPlayer instance has been disposed.');
  }

  private sleep(ms: number) {
    return new Promise<void>((r) => setTimeout(r, ms));
  }
}
